// A figure clicked through to its tickets: which Tickets filters stand for a
// matrix cell, a headline figure or a waiting band, and how those filters read
// as chips. The server evaluates them on the report's own working set
// (internal/store/ticketreport.go), so the count it answers is the figure's.

import type { StaffKey, StaffLanguage } from '../lib/staffText'
import type { BandSpec, ReportRow } from '../lib/types'
import { windowLabel, type SayFn } from './insightBands'
import { CALENDAR, PERIODS, applies, isTicketDim, periodRange, rowsOf, shiftDays, spotDims, type BreakDim, type Layout, type MeasureId, type PeriodId, type Source, type Spot } from './insightCalc'
import { memberLabel } from './insightDims'
import type { TicketFilters } from './ticketReport'

export type DrillAt = { spot: Spot; measure?: MeasureId; band?: { kind: 'wait'; index: number } }
export type DrillBase = { branchId: string; from: string; to: string; before: { from: string; to: string } }

/** The ticket-level condition each measure counts (report.Count*): a rate opens
 *  what it counts, a waiting time the tickets that were called, the service level
 *  the tickets it is judged on. */
const COUNTED: Partial<Record<MeasureId, string>> = {
  served: 'served',
  noShow: 'noShow',
  noShowRate: 'noShow',
  voided: 'voided',
  cancelledBeforeCall: 'cancelledBeforeCall',
  left: 'left',
  unserved: 'unserved',
  stillOpen: 'stillOpen',
  recalled: 'recalled',
  wait: 'called',
  level: 'measured',
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/
const clock = (min: number) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`
/** "10:45" as minutes of the day, when it starts a step-minute window; else null. */
function windowStart(key: string, step: number): number | null {
  const m = /^(\d{2}):(\d{2})$/.exec(key)
  if (!m) return null
  const min = Number(m[1]) * 60 + Number(m[2])
  return Number(m[1]) < 24 && min % step === 0 ? min : null
}
const lastOfMonth = (ym: string) => {
  const d = new Date(Date.UTC(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)), 0))
  return d.toISOString().slice(0, 10)
}
/** [a, b] cut to [from, to]; null when nothing of it is left. */
function cut(a: string, b: string, range: { from: string; to: string }): [string, string] | null {
  const x = a > range.from ? a : range.from
  const y = b < range.to ? b : range.to
  return x <= y ? [x, y] : null
}

/** The issue window [start, end) minutes on top of f's own, or null where they do not meet. */
function issued(f: TicketFilters, start: number, end: number): TicketFilters | null {
  const minute = (x: string | undefined, none: number) => (x ? Number(x.slice(0, 2)) * 60 + Number(x.slice(3, 5)) : none)
  const a = Math.max(start, minute(f.issuedFrom, 0))
  const b = Math.min(end, minute(f.issuedTo, 1440))
  return a < b ? { ...f, issuedFrom: clock(a), issuedTo: clock(b) } : null
}

/** One grouping row's tickets, as filters on top of f; null where its key has no drill.
 *  A second grouping narrows the first's dates or times, never widens them. */
function byRow(f: TicketFilters, dim: BreakDim, key: string): TicketFilters | null {
  const range = { from: f.from, to: f.to }
  switch (dim) {
    case 'service':
      return { ...f, arrival: key || 'none' }
    case 'outcome':
      return { ...f, outcome: key || 'none' }
  }
  if (!key) return null
  switch (dim) {
    case 'serviceWorked':
      return { ...f, service: key }
    case 'desk':
      return { ...f, desk: key }
    case 'person':
      return { ...f, staff: key }
    case 'mark':
      return { ...f, mark: key }
    case 'hour': {
      const h = /^\d{2}$/.test(key) ? Number(key) : NaN
      return h >= 0 && h < 24 ? issued(f, h * 60, h * 60 + 60) : null
    }
    case 'interval15':
    case 'interval30': {
      const step = dim === 'interval15' ? 15 : 30
      const start = windowStart(key, step)
      return start === null ? null : issued(f, start, start + step)
    }
    case 'day': {
      const span = ISO_DAY.test(key) ? cut(key, key, range) : null
      return span ? { ...f, from: span[0], to: span[1] } : null
    }
    case 'week': {
      const span = ISO_DAY.test(key) ? cut(key, shiftDays(key, 6), range) : null
      return span ? { ...f, from: span[0], to: span[1] } : null
    }
    case 'month': {
      const span = /^\d{4}-\d{2}$/.test(key) ? cut(`${key}-01`, lastOfMonth(key), range) : null
      return span ? { ...f, from: span[0], to: span[1] } : null
    }
    case 'weekday': {
      const n = /^[1-7]$/.test(key) ? Number(key) : 0
      return n && (!f.weekday || f.weekday === n) ? { ...f, weekday: n } : null
    }
    case 'channel':
      return { ...f, sources: [key] }
    case 'priority':
      return { ...f, priority: key }
    case 'language':
      return { ...f, language: key }
    case 'call':
      return { ...f, call: key }
  }
  return null
}

/**
 * The Tickets filters for the figure at `at`, or null when there is nothing to
 * open: a dash, a Change cell, a calendar row in the period before, a wait band
 * on a grouping that has no waits.
 *
 * A measure adds the condition it counts. On a work grouping (a desk, a person,
 * an outcome) Served is the tickets with work there that ended, the total's
 * "served" counted there; a handling time is the work that ended.
 */
export function drillFilters(base: DrillBase, at: DrillAt, layout: Layout, bands: BandSpec): TicketFilters | null {
  const { spot, measure, band } = at
  const hasPeriod = layout.rows.includes('period') || layout.cols.includes('period')
  if (spot.period === 'change' || (hasPeriod && !spot.period)) return null
  const range = spot.period === 'before' ? base.before : base
  const dims = spotDims(spot)
  if (spot.also && spot.period === 'before') return null
  if (dims.some((d) => spot.period === 'before' && CALENDAR.has(d.dim))) return null
  if (measure && !dims.every((d) => applies(measure, d.dim))) return null
  if (band && !dims.every((d) => applies('wait', d.dim))) return null

  let f: TicketFilters | null = { branches: [base.branchId], from: range.from, to: range.to, service: '', staff: '', result: '', channel: '' }
  for (const d of dims) f = f && byRow(f, d.dim, d.key)
  if (!f) return null

  const onTickets = dims.every((d) => isTicketDim(d.dim))
  const counted = measure && onTickets ? COUNTED[measure] : undefined
  if (counted) f.counted = counted
  if (measure === 'handling' || (measure === 'served' && !onTickets)) f.ended = true

  if (band) {
    const edges = bands.waitEdges
    const i = band.index
    if (!Number.isInteger(i) || i < 0 || i > edges.length + 1) return null
    f.wait =
      i === edges.length + 1
        ? { by: bands.waitBy, lo: 0, hi: 0, notCalled: true }
        : { by: bands.waitBy, lo: i === 0 ? 0 : edges[i - 1]!, hi: i < edges.length ? edges[i]! : 0 }
  }
  return f
}

/** What the clicked figure counts, so the Tickets tab can show it first-hand: a
 *  handling time counts pieces of work (the tab's rows), Served and every other
 *  count tickets; a work grouping's row with no measure is its pieces of work. */
export const countsOf = (spot: Spot, measure?: MeasureId): 'tickets' | 'rows' =>
  measure === 'handling' || (!measure && spotDims(spot).some((d) => !isTicketDim(d.dim))) ? 'rows' : 'tickets'

export type RowFind = (spot: Spot) => ReportRow | undefined

/** The report row a spot names, by its member key (key || label), in the period it
 *  names. Each grouping is indexed once, so a matrix's cells look up in O(1). */
export function rowFinder(src: { now: Source | null; before: Source | null }): RowFind {
  const kept = new Map<string, Map<string, ReportRow>>()
  return (spot) => {
    if (!spot.dim || spot.key === undefined) return undefined
    const period = spot.period === 'before' ? 'before' : 'now'
    const id = `${period}|${spot.dim}`
    let index = kept.get(id)
    if (!index) {
      const s = src[period]
      index = new Map((s ? (rowsOf(s, spot.dim) ?? []) : []).map((r) => [r.key || r.label, r] as const))
      // A fetched grouping not yet arrived is not remembered as empty.
      if (s && rowsOf(s, spot.dim)) kept.set(id, index)
    }
    return index.get(spot.key)
  }
}

/** The spot with the server's own key. The matrix keys a member key||label, so a
 *  row the server sent with no key (no service yet, no outcome) is keyed by its label. */
export function serverSpot(find: RowFind, spot: Spot): Spot {
  const row = find(spot)
  const out = row ? { ...spot, key: row.key } : spot
  const also = spot.also && find({ period: spot.period, dim: spot.also.dim, key: spot.also.key })
  return also && spot.also ? { ...out, also: { dim: spot.also.dim, key: also.key } } : out
}

/** The filter a grouping row sets, for the names a click knows (a desk's name, a service's). */
export const FIELD_OF: Partial<Record<BreakDim, keyof TicketFilters>> = {
  service: 'arrival',
  serviceWorked: 'service',
  desk: 'desk',
  person: 'staff',
  outcome: 'outcome',
  mark: 'mark',
}

export type Chip = { id: keyof TicketFilters; name: string; text: string }

const COUNTED_KEY: Record<string, StaffKey> = {
  served: 'reports.served',
  noShow: 'insight.m.noShow',
  voided: 'reports.voided',
  cancelledBeforeCall: 'reports.cancelledBeforeCall',
  left: 'reports.leftFromPhone',
  unserved: 'reports.leftUnservedShort',
  stillOpen: 'reports.stillOpen',
  recalled: 'insight.m.recalled',
  called: 'insight.chip.called',
  measured: 'reports.serviceLevel',
}

/**
 * The drill's filters as chips, in the reader's language: one for each filter
 * the Tickets tab has no control of its own for (branch, period, service,
 * person, result and channel have theirs). `names` are the words the click
 * knew, a desk's or a service's name, where the filter holds only an id. A
 * chip whose value says what it is has no name.
 */
export function chipsOf(f: TicketFilters, say: SayFn, lang: StaffLanguage, names: Partial<Record<keyof TicketFilters, string>> = {}): Chip[] {
  const out: Chip[] = []
  const add = (id: keyof TicketFilters, name: StaffKey | null, text: string) => out.push({ id, name: name ? say(name) : '', text: names[id] || text })
  const member = (dim: BreakDim, key: string) => memberLabel(dim, key, lang, say)
  if (f.arrival) add('arrival', 'insight.dim.service', f.arrival === 'none' ? say('insight.notAtDesk') : f.arrival)
  if (f.desk) add('desk', 'insight.dim.desk', f.desk)
  // The person has a picker of its own, but a figure's person reads as a chip too,
  // as its desk or service does: the drill says what it opened.
  if (f.staff && names.staff) add('staff', 'insight.dim.person', f.staff)
  if (f.outcome) add('outcome', 'insight.dim.outcome', f.outcome === 'none' ? say('reports.nothingRecorded') : f.outcome)
  if (f.mark) add('mark', 'insight.dim.mark', f.mark)
  if (f.sources?.length) add('sources', 'insight.dim.channel', f.sources.map((s) => member('channel', s)).join(', '))
  if (f.priority) add('priority', 'common.priority', member('priority', f.priority))
  if (f.language) add('language', 'entry.language', member('language', f.language))
  if (f.call) add('call', 'insight.dim.call', member('call', f.call))
  if (f.weekday) add('weekday', null, member('weekday', String(f.weekday)))
  if (f.issuedFrom || f.issuedTo) add('issuedFrom', null, `${f.issuedFrom || '00:00'}–${f.issuedTo || '24:00'}`)
  if (f.wait) {
    const w = f.wait
    if (w.notCalled) add('wait', null, say('insight.band.left'))
    else add('wait', 'insight.m.wait', windowLabel(w.by, w.lo, w.hi, say))
  }
  if (f.counted) add('counted', null, COUNTED_KEY[f.counted] ? say(COUNTED_KEY[f.counted]!) : f.counted)
  if (f.ended) add('ended', null, say('reports.served'))
  if (f.booked) out.push({ id: 'booked', name: say('bookingsReport.tab'), text: [say('bookingsReport.came'), names.booked].filter(Boolean).join(' · ') })
  return out
}

/** The filters without one chip's; the issue window goes as one. */
export function withoutChip(f: TicketFilters, id: keyof TicketFilters): TicketFilters {
  const out: TicketFilters = { ...f }
  delete out[id]
  if (id === 'issuedFrom') delete out.issuedTo
  return out
}

/** The period button that reads exactly these dates, else the dates themselves. */
export function periodFor(from: string, to: string, today: string): { period: PeriodId; custom: [string, string] } {
  const custom: [string, string] = [from, to]
  const hit = PERIODS.find(([p]) => {
    if (p === 'custom') return false
    const [a, b] = periodRange(p, today, custom)
    return a === from && b === to
  })
  return { period: hit ? hit[0] : 'custom', custom }
}
